// sagas/identity/guestDocumentSagas.ts
import { all, fork, put, takeEvery, call, delay } from 'redux-saga/effects';
import dayjs from 'dayjs';
import { userSignOut } from '../../actions/Auth';
import {
  fetchPassportDataSuccess,
  fetchPassportDataFailure,
  fetchGuestDataSuccess,
  fetchGuestDataFailure,
  saveGuestDocumentSuccess,
  saveGuestDocumentFailure,
  deleteSingleDocumentSuccess,
  deleteSingleDocumentFailure,
  deleteGroupDocumentSuccess,
  deleteGroupDocumentFailure,
  deleteMoreGuestSuccess,
  deleteMoreGuestFailure,
  scanPassportSuccess,
  scanPassportFailure,
  scanIdCardSuccess,
  scanIdCardFailure,
  FetchPassportDataRequestAction,
  FetchGuestDataRequestAction,
  SaveGuestDocumentRequestAction,
  DeleteSingleDocumentRequestAction,
  DeleteGroupDocumentRequestAction,
  DeleteMoreGuestRequestAction,
  ScanPassportRequestAction,
  ScanIdCardRequestAction,
  FETCH_PASSPORT_DATA_REQUEST,
  FETCH_GUEST_DATA_REQUEST,
  SAVE_GUEST_DOCUMENT_REQUEST,
  DELETE_SINGLE_DOCUMENT_REQUEST,
  DELETE_GROUP_DOCUMENT_REQUEST,
  DELETE_MORE_GUEST_REQUEST,
  SCAN_PASSPORT_REQUEST,
  SCAN_ID_CARD_REQUEST,
} from '../../actions/identity/guestDocumentActions';
import { API_URL_V1, API_PMS_HEADERS, BASE_URL_LOCAL, LOCAL_HEADERS } from 'constants/ApiSetting';
import { getItems } from 'util/localStorage';
import { APIGetRequest, APIPostRequest } from 'util/connection';
import { ApiResponse } from 'constants/ActionTypes';
import noImg from 'assets/images/default-user-icon.jpg';

// Fetch Passport Data
function* fetchPassportDataSaga(action: FetchPassportDataRequestAction): Generator<any, void, any> {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(fetchPassportDataFailure('Authentication token is missing.'));
      yield put(userSignOut());
      return;
    }

    const url = `${API_URL_V1}pms/addon/guestdocument/getdocument?isAssigned=${action.payload.isAssigned}`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield APIGetRequest(url, API_PMS_HEADERS);

    // APIGetRequest returns response.data on success, or {status, data} on error
    // Check for error response first
    if (response && typeof response === 'object' && 'status' in response) {
      if (response.status === 403) {
        yield put(userSignOut());
        yield put(fetchPassportDataFailure('Access denied'));
        return;
      }

      if (response.status !== 200) {
        yield put(
          fetchPassportDataFailure(response.data?.message || 'Failed to fetch passport data.')
        );
        return;
      }
    }

    // Handle response structure - could be array or object
    let dataNode = null;

    // If response is array directly, skip passport data (this endpoint should return object)
    if (Array.isArray(response)) {
      yield put(fetchPassportDataSuccess({}));
      return;
    }

    // Extract data node from various structures
    if (response?.data?.data) {
      dataNode = response.data.data;
    } else if (response?.data) {
      dataNode = response.data;
    } else {
      dataNode = response;
    }

    if (!dataNode) {
      yield put(fetchPassportDataSuccess({}));
      return;
    }

    // Helper: normalize array/object collections into a plain array
    const toArray = (val: any): any[] => {
      if (Array.isArray(val)) return val;
      if (val && typeof val === 'object') return Object.values(val);
      return [];
    };

    const formattedGroupedData: { [key: string]: any[] } = {};

    // Three tolerant shapes:
    const maybeGroups = Array.isArray(dataNode?.groups)
      ? dataNode.groups
      : Array.isArray(dataNode)
        ? dataNode
        : null;

    if (maybeGroups) {
      // Array of groups
      for (const grp of maybeGroups) {
        const tagName = String(grp?.tag ?? 'Ungrouped');
        const docs = toArray(grp?.documents);
        formattedGroupedData[tagName] = docs.map((item: any, index: number) => ({
          key: `${tagName}-${index}`,
          guestDocId: item.guest_doc_id,
          fullName: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
          firstName: item.first_name || '',
          lastName: item.last_name || '',
          gender: item.sex || 'N/A',
          passport: item.passport || 'N/A',
          country: item.country || 'N/A',
          nationality: item.nationality || 'N/A',
          birthDate: item.birth_date || 'N/A',
          profile: item.photo_url || noImg,
          ImgProfile: item.image_url || noImg,
          address: (() => {
            const raw = item.address ?? item.addr ?? null;
            try {
              if (typeof raw === 'string' && raw.trim().startsWith('{')) {
                const a = JSON.parse(raw);
                return [a.addr_no, a.subdistrict, a.district, a.province]
                  .filter(Boolean)
                  .join(', ');
              }
            } catch { }
            const concat12 = [item.address1, item.address2].filter(Boolean).join(', ');
            return (typeof raw === 'string' ? raw : concat12) || '';
          })(),
          address1: item.address1 ?? undefined,
          address2: item.address2 ?? undefined,
          contact: item.contact ?? undefined,
          doc_type: item.doc_type || 'N/A',
          guest_id: item.guest_id,
          image_url: item.image_url,
          title: item.title || item.title_name || item.title_id || '',
          groupName: tagName,
        }));
      }
    } else if (typeof dataNode === 'object' && (dataNode.tag || dataNode.documents)) {
      // Single group node
      const tagName = String(dataNode.tag ?? 'Ungrouped');
      const docs = toArray(dataNode.documents);
      formattedGroupedData[tagName] = docs.map((item: any, index: number) => ({
        key: `${tagName}-${index}`,
        guestDocId: item.guest_doc_id,
        fullName: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
        firstName: item.first_name || '',
        lastName: item.last_name || '',
        gender: item.sex || 'N/A',
        passport: item.passport || 'N/A',
        country: item.country || 'N/A',
        nationality: item.nationality || 'N/A',
        birthDate: item.birth_date || 'N/A',
        profile: item.photo_url || noImg,
        ImgProfile: item.image_url || noImg,
        address: (() => {
          const raw = item.address ?? item.addr ?? null;
          try {
            if (typeof raw === 'string' && raw.trim().startsWith('{')) {
              const a = JSON.parse(raw);
              return [a.addr_no, a.subdistrict, a.district, a.province].filter(Boolean).join(', ');
            }
          } catch { }
          const concat12 = [item.address1, item.address2].filter(Boolean).join(', ');
          return (typeof raw === 'string' ? raw : concat12) || '';
        })(),
        address1: item.address1 ?? undefined,
        address2: item.address2 ?? undefined,
        contact: item.contact ?? undefined,
        doc_type: item.doc_type || 'N/A',
        guest_id: item.guest_id,
        image_url: item.image_url,
        title: item.title || item.title_name || item.title_id || '',
        groupName: tagName,
      }));
    } else if (typeof dataNode === 'object') {
      // Legacy keyed groups
      Object.entries(dataNode as Record<string, any[]>).forEach(([groupKey, items]) => {
        const docs = toArray(items);
        formattedGroupedData[groupKey] = docs.map((item: any, index: number) => ({
          key: `${groupKey}-${index}`,
          guestDocId: item.guest_doc_id,
          fullName: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
          firstName: item.first_name || '',
          lastName: item.last_name || '',
          gender: item.sex || 'N/A',
          passport: item.passport || 'N/A',
          country: item.country || 'N/A',
          nationality: item.nationality || 'N/A',
          birthDate: item.birth_date || 'N/A',
          profile: item.photo_url || noImg,
          ImgProfile: item.image_url || noImg,
          address: (() => {
            const raw = item.address ?? item.addr ?? null;
            try {
              if (typeof raw === 'string' && raw.trim().startsWith('{')) {
                const a = JSON.parse(raw);
                return [a.addr_no, a.subdistrict, a.district, a.province]
                  .filter(Boolean)
                  .join(', ');
              }
            } catch { }
            const concat12 = [item.address1, item.address2].filter(Boolean).join(', ');
            return (typeof raw === 'string' ? raw : concat12) || '';
          })(),
          address1: item.address1 ?? undefined,
          address2: item.address2 ?? undefined,
          contact: item.contact ?? undefined,
          doc_type: item.doc_type || 'N/A',
          guest_id: item.guest_id,
          image_url: item.image_url,
          title: item.title || item.title_name || item.title_id || '',
          groupName: groupKey,
        }));
      });
    }

    yield put(fetchPassportDataSuccess(formattedGroupedData));
    yield delay(100);
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(fetchPassportDataFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to fetch passport data.';
    yield put(fetchPassportDataFailure(errorMessage));
    yield delay(100);
  }
}

// Fetch Guest Data
function* fetchGuestDataSaga(action: FetchGuestDataRequestAction): Generator<any, void, any> {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(fetchGuestDataFailure('Authentication token is missing.'));
      yield put(userSignOut());
      return;
    }

    const url = action.payload.arrivalDate
      ? `${API_URL_V1}pms/addon/guestdocument/getregistration?arrivaldate=${action.payload.arrivalDate}`
      : `${API_URL_V1}pms/addon/guestdocument/getregistration`;

    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield APIGetRequest(url, API_PMS_HEADERS);

    // APIGetRequest returns response.data on success, or {status, data} on error
    // Check for error response first
    if (response && typeof response === 'object' && 'status' in response) {
      if (response.status === 403) {
        yield put(userSignOut());
        yield put(fetchGuestDataFailure('Access denied'));
        return;
      }

      if (response.status !== 200) {
        yield put(fetchGuestDataFailure(response.data?.message || 'Failed to fetch guest data.'));
        return;
      }
    }

    // APIGetRequest returns response.data directly on success (not axios response object)
    // Handle different response structures
    let rawData = [];

    // Case 1: response is array directly (most common - API returns array)
    if (Array.isArray(response)) {
      rawData = response;
    }
    // Case 2: response.data.result_data (nested structure)
    else if (
      response &&
      typeof response === 'object' &&
      response?.data?.result_data &&
      Array.isArray(response.data.result_data)
    ) {
      rawData = response.data.result_data;
    }
    // Case 3: response.data is array
    else if (
      response &&
      typeof response === 'object' &&
      response?.data &&
      Array.isArray(response.data)
    ) {
      rawData = response.data;
    }
    // Case 4: response.data.data.result_data (deeply nested)
    else if (
      response &&
      typeof response === 'object' &&
      response?.data?.data?.result_data &&
      Array.isArray(response.data.data.result_data)
    ) {
      rawData = response.data.data.result_data;
    }

    if (!Array.isArray(rawData) || rawData.length === 0) {
      yield put(fetchGuestDataSuccess([]));
      return;
    }

    // Format guest data
    const formattedGuestData = rawData.map((group: any, groupIndex: number) => ({
      key: `group-${group.group_id || groupIndex}`,
      groupName: group.group_name || 'N/A',
      children: group.rooms
        .filter((room: any) => {
          if (!action.payload.arrivalDate) return true;
          const roomArrival = dayjs(room.arrival, 'DD-MM-YYYY').format('YYYY-MM-DD');
          return roomArrival === action.payload.arrivalDate;
        })
        .map((room: any, roomIndex: number) => ({
          key: `group-${group.group_id || groupIndex}-room-${room.room_id}-${room.registration_id}-${roomIndex}`,
          room: room.room_number || 'N/A',
          title: room.title || '',
          guest: `${room.first_name || ''} ${room.last_name || ''}`.trim() || 'N/A',
          status: room.status || 'N/A',
          arrival: room.arrival || 'N/A',
          departure: room.departure || 'N/A',
          nationality: room.nationality || 'UNKNOWN',
          address: room.address || '',
          passport: room.passport || '',
          pax: room.pax || 'N/A',
          registration_id: room.registration_id || 'N/A',
          birthdate: room.birthdate || 'N/A',
          country: room.country || 'N/A',
          photo_url: room.photo_url || noImg,
          image_url: room.image_url || noImg,
          accno: room.accno || 'N/A',
          sex: room.sex || 'N/A',
          guest_id: room.guest_id || 'N/A',
          first_name: room.first_name || '',
          last_name: room.last_name || '',
          moreguest:
            room.moreguest && Array.isArray(room.moreguest)
              ? room.moreguest.map((guest: any, guestIndex: number) => ({
                key: `group-${group.group_id || groupIndex}-room-${room.room_id}-guest-${guest.guest_id}-${guestIndex}-${roomIndex}`,
                title: guest.title || '',
                guest: `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'N/A',
                nationality: guest.nationality || 'N/A',
                passport: guest.passport || '',
                sex: guest.sex || 'N/A',
                birthdate: guest.birthdate || 'N/A',
                country: guest.country || 'N/A',
                status: guest.status || 'N/A',
                guest_id: guest.guest_id || 'N/A',
                photo_url: guest.photo_url || noImg,
                image_url: guest.image_url || noImg,
                address: guest.address || '',
                first_name: guest.first_name || '',
                last_name: guest.last_name || '',
              }))
              : [],
          showMoreGuest: false,
          summoreguest: room.summoreguest || 0,
        })),
    }));

    yield put(fetchGuestDataSuccess(formattedGuestData));
    yield delay(100);
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(fetchGuestDataFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to fetch guest data.';
    yield put(fetchGuestDataFailure(errorMessage));
    yield delay(100);
  }
}

// Save Guest Document
function* saveGuestDocumentSaga(action: SaveGuestDocumentRequestAction): Generator<any, void, any> {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(saveGuestDocumentFailure('Authentication token is missing.'));
      yield put(userSignOut());
      return;
    }

    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const { tempGuestData, tempMoreGuestData, arrivalDateState, selectedDate, isUpdateMoreGuest } =
      action.payload;

    // 1️⃣ Replace more guest if needed
    if (isUpdateMoreGuest && tempMoreGuestData.length > 0) {
      const replacePayload = tempMoreGuestData.map((item) => ({
        arrival_date: dayjs(item.arrival_date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        register_id: item.register_id,
        old: { guest_id: item.guest_id_old },
        new: { guest_doc_id: item.guest_id_new },
      }));

      const replaceResponse: ApiResponse = yield APIPostRequest(
        `${API_URL_V1}pms/addon/guestdocument/replacemoreguest`,
        { data: replacePayload },
        API_PMS_HEADERS
      );

      if (replaceResponse.status === 403) {
        yield put(userSignOut());
        yield put(saveGuestDocumentFailure('Access denied'));
        return;
      }

      if (replaceResponse?.data?.status !== 200) {
        yield put(
          saveGuestDocumentFailure(replaceResponse?.data?.message || 'Failed to replace data.')
        );
        return;
      }
    }

    // 2️⃣ Save assign document if needed
    if (tempGuestData.length > 0) {
      const assignPayload = tempGuestData.map((item) => {
        if (item.mode === 'MOREGUEST') {
          return {
            mode: 'MOREGUEST',
            register_id: item.register_id !== 'N/A' ? item.register_id : null,
            arrival_date:
              arrivalDateState ||
              (selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null) ||
              '',
            moreguest:
              item.moreguest?.map((guest: any) => ({
                guest_doc_id: guest.guest_doc_id,
              })) || [],
          };
        } else {
          return {
            mode: item.mode,
            register_id: item.register_id,
            guest_doc_id: item.guest_doc_id,
            arrival_date:
              arrivalDateState ||
              (selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null) ||
              '',
          };
        }
      });

      // Validate arrival_date
      const hasEmptyArrival = assignPayload.some(
        (it: any) => !it.arrival_date || String(it.arrival_date).trim() === ''
      );
      if (hasEmptyArrival) {
        yield put(saveGuestDocumentFailure('Please select an Arrival date before saving.'));
        return;
      }

      const saveResponse: ApiResponse = yield APIPostRequest(
        `${API_URL_V1}pms/addon/guestdocument/saveassigndocument`,
        { data: assignPayload },
        API_PMS_HEADERS
      );

      if (saveResponse.status === 403) {
        yield put(userSignOut());
        yield put(saveGuestDocumentFailure('Access denied'));
        return;
      }

      const apiData = saveResponse?.data?.data ?? saveResponse?.data;
      const resultCode = String(apiData?.result_code ?? '');
      const resultMsg = apiData?.result_msg || 'Unknown response';

      if (resultCode !== '0') {
        yield put(saveGuestDocumentFailure(resultMsg));
        return;
      }

      const effectiveArrivalDate =
        arrivalDateState || (selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null) || '';
      yield put(
        saveGuestDocumentSuccess({
          message: 'Guest data has been successfully saved.',
          arrivalDate: effectiveArrivalDate,
        })
      );
      yield delay(100);
    } else {
      yield put(saveGuestDocumentSuccess({ message: 'Data saved successfully.' }));
      yield delay(100);
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(saveGuestDocumentFailure('Access denied'));
      return;
    }
    const errorMessage = error.response?.data?.message || error.message || 'Failed to save data.';
    yield put(saveGuestDocumentFailure(errorMessage));
  }
}

// Delete Single Document
function* deleteSingleDocumentSaga(
  action: DeleteSingleDocumentRequestAction
): Generator<any, void, any> {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(deleteSingleDocumentFailure('Authentication token is missing.'));
      yield put(userSignOut());
      return;
    }

    const url = `${API_URL_V1}pms/addon/guestdocument/delete`;
    const payload = {
      mode: 's',
      data: {
        tag: '',
        guest_doc_id: action.payload.guestDocId,
      },
    };

    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield APIPostRequest(url, payload, API_PMS_HEADERS);

    if (response.status === 403) {
      yield put(userSignOut());
      yield put(deleteSingleDocumentFailure('Access denied'));
      return;
    }

    if (response.status === 200) {
      yield put(deleteSingleDocumentSuccess(action.payload.groupKey, action.payload.guestDocId));
      yield delay(100);
    } else {
      yield put(
        deleteSingleDocumentFailure(response.data?.message || 'Failed to delete the document.')
      );
      yield delay(100);
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(deleteSingleDocumentFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to delete document.';
    yield put(deleteSingleDocumentFailure(errorMessage));
    yield delay(100);
  }
}

// Delete Group Document
function* deleteGroupDocumentSaga(
  action: DeleteGroupDocumentRequestAction
): Generator<any, void, any> {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(deleteGroupDocumentFailure('Authentication token is missing.'));
      yield put(userSignOut());
      return;
    }

    if (action.payload.itemsToDelete.length === 0) {
      yield put(deleteGroupDocumentSuccess(action.payload.groupKey));
      return;
    }

    const url = `${API_URL_V1}pms/addon/guestdocument/delete`;
    const payload = {
      mode: 'g',
      data: action.payload.itemsToDelete.map((item) => ({
        tag: action.payload.groupKey,
        guest_doc_id: item.guestDocId || null,
      })),
    };

    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield APIPostRequest(url, payload, API_PMS_HEADERS);

    if (response.status === 403) {
      yield put(userSignOut());
      yield put(deleteGroupDocumentFailure('Access denied'));
      return;
    }

    if (response.status === 200) {
      yield put(deleteGroupDocumentSuccess(action.payload.groupKey));
      yield delay(100);
    } else {
      yield put(
        deleteGroupDocumentFailure(response.data?.message || 'Failed to delete the group.')
      );
      yield delay(100);
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(deleteGroupDocumentFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to delete group.';
    yield put(deleteGroupDocumentFailure(errorMessage));
    yield delay(100);
  }
}

// Delete More Guest
function* deleteMoreGuestSaga(action: DeleteMoreGuestRequestAction): Generator<any, void, any> {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    if (!access_token) {
      yield put(deleteMoreGuestFailure('Authentication token is missing.'));
      yield put(userSignOut());
      return;
    }

    const url = `${API_URL_V1}pms/addon/guestdocument/deleteMoreGuest`;
    const payload = {
      register_id: action.payload.registerId,
      guest_id: action.payload.guestId,
      arrival_date: dayjs(action.payload.arrivalDate, 'YYYY-MM-DD').format('YYYY-MM-DD'),
    };

    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield APIPostRequest(url, payload, API_PMS_HEADERS);

    if (response.status === 403) {
      yield put(userSignOut());
      yield put(deleteMoreGuestFailure('Access denied'));
      return;
    }

    if (response.status === 200) {
      yield put(deleteMoreGuestSuccess(action.payload.registerId, action.payload.guestId));
      yield delay(100);
    } else {
      yield put(
        deleteMoreGuestFailure(response.data?.message || 'Failed to delete the more guest.')
      );
      yield delay(100);
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(deleteMoreGuestFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to delete more guest.';
    yield put(deleteMoreGuestFailure(errorMessage));
    yield delay(100);
  }
}

// Scan Passport
function* scanPassportSaga(action: ScanPassportRequestAction): Generator<any, void, any> {
  try {
    const accessToken = localStorage.getItem('access_token') || '';
    const url = `${BASE_URL_LOCAL}/api/Passport/readPassport?tagName=${encodeURIComponent(
      action.payload.groupTagPassport || action.payload.tagName || ''
    )}&accessToken=${encodeURIComponent(accessToken)}`;

    const response: ApiResponse = yield APIPostRequest(
      url,
      {},
      {
        headers: {
          ...LOCAL_HEADERS.headers,
          'x-api-key': 'nEKDrq7SP2U9mvar2D634Bo9Yp2XUAFa',
        },
      }
    );

    if (response.status === 403) {
      yield put(userSignOut());
      yield put(scanPassportFailure('Access denied'));
      return;
    }

    const result = response.data;
    if (result?.data?.result_code === '0') {
      yield put(scanPassportSuccess(result.data.result_data));
      yield delay(100);
    } else {
      yield put(scanPassportFailure(result?.data?.result_msg || 'Unknown error'));
      yield delay(100);
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(scanPassportFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to scan passport.';
    yield put(scanPassportFailure(errorMessage));
    yield delay(100);
  }
}

// Scan ID Card
function* scanIdCardSaga(action: ScanIdCardRequestAction): Generator<any, void, any> {
  try {
    const accessToken = localStorage.getItem('access_token') || '';
    // const url = `${BASE_URL_LOCAL}/api/IDCard/readIDCard?tagName=${encodeURIComponent(
    //   action.payload.groupTagPassport || action.payload.tagName || ''
    // )}&accessToken=${encodeURIComponent(accessToken)}`;

    const url = `${BASE_URL_LOCAL}/api/IDCard/readIDCard`;
    //

    const response: ApiResponse = yield APIPostRequest(
      url,
      { tagName: action.payload.groupTagPassport || action.payload.tagName || '' },
      {
        headers: {
          ...LOCAL_HEADERS.headers,
          // 'x-api-key': 'nEKDrq7SP2U9mvar2D634Bo9Yp2XUAFa',
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.status === 403) {
      yield put(userSignOut());
      yield put(scanIdCardFailure('Access denied'));
      return;
    }

    if (response.status === 500) {
      yield put(scanIdCardFailure(response.data?.message || 'Failed to scan ID card.'));
      return;
    }

    const result = response.data;
    console.log('result', result);
    if (result?.result_code === '0') {
      yield put(scanIdCardSuccess(result?.result_data));
      yield delay(100);
    } else {
      yield put(scanIdCardFailure(result?.result_msg || 'Unknown error'));
      yield delay(100);
    }
  } catch (error: any) {
    console.log('error', error);

    if (error.response?.status === 403) {
      yield put(userSignOut());
      yield put(scanIdCardFailure('Access denied'));
      yield delay(100);
      return;
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to scan ID card.';
    yield put(scanIdCardFailure(errorMessage));
    yield delay(100);
  }
}

// Watchers
function* fetchPassportDataWatcher() {
  yield takeEvery(FETCH_PASSPORT_DATA_REQUEST, fetchPassportDataSaga);
}

function* fetchGuestDataWatcher() {
  yield takeEvery(FETCH_GUEST_DATA_REQUEST, fetchGuestDataSaga);
}

function* saveGuestDocumentWatcher() {
  yield takeEvery(SAVE_GUEST_DOCUMENT_REQUEST, saveGuestDocumentSaga);
}

function* deleteSingleDocumentWatcher() {
  yield takeEvery(DELETE_SINGLE_DOCUMENT_REQUEST, deleteSingleDocumentSaga);
}

function* deleteGroupDocumentWatcher() {
  yield takeEvery(DELETE_GROUP_DOCUMENT_REQUEST, deleteGroupDocumentSaga);
}

function* deleteMoreGuestWatcher() {
  yield takeEvery(DELETE_MORE_GUEST_REQUEST, deleteMoreGuestSaga);
}

function* scanPassportWatcher() {
  yield takeEvery(SCAN_PASSPORT_REQUEST, scanPassportSaga);
}

function* scanIdCardWatcher() {
  yield takeEvery(SCAN_ID_CARD_REQUEST, scanIdCardSaga);
}

export default function* guestDocumentSagas() {
  yield all([
    fork(fetchPassportDataWatcher),
    fork(fetchGuestDataWatcher),
    fork(saveGuestDocumentWatcher),
    fork(deleteSingleDocumentWatcher),
    fork(deleteGroupDocumentWatcher),
    fork(deleteMoreGuestWatcher),
    fork(scanPassportWatcher),
    fork(scanIdCardWatcher),
  ]);
}
